import { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckIcon from '@mui/icons-material/Check';

import { useInformarPagoEmpresa } from '../../hooks/usePagos';
import type { Cupon } from '../../types/cupon';
import { formatCurrency, todayLocal } from '../../utils/format';
import { mapSupabaseError } from '../../utils/errores';

/**
 * La empresa informa una transferencia y adjunta el comprobante (ADR #043).
 * Crea un pago PENDIENTE: ATSA lo concilia y confirma. La empresa nunca
 * acredita el pago. Reglas espejo de las del RPC `informar_pago_empresa`.
 */

const MONTO_MAX_PAGO = 999_999_999.99;
const ACCEPT_TYPES = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';
const TIPOS_OK = ['application/pdf', 'image/png', 'image/jpeg'];
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

function makeSchema(cupon: Cupon) {
  return z.object({
    monto_pagado: z
      .number({ invalid_type_error: 'Ingresá un monto válido' })
      .positive('El monto debe ser mayor a 0')
      .max(MONTO_MAX_PAGO, `El monto no puede superar $${MONTO_MAX_PAGO.toLocaleString('es-AR')}`),
    fecha_pago: z
      .string()
      .min(1, 'Fecha de la transferencia requerida')
      .refine(
        (v) => v >= cupon.fecha_emision,
        `La fecha no puede ser anterior a la emisión del cupón (${cupon.fecha_emision})`,
      )
      .refine((v) => v <= todayLocal(), 'La fecha no puede ser futura'),
    nro_comprobante: z
      .string()
      .trim()
      .min(3, 'Indicá el N° de comprobante (mínimo 3 caracteres)')
      .max(60, 'Máximo 60 caracteres'),
    medio_pago: z
      .string()
      .trim()
      .min(3, 'Indicá el banco o billetera desde la que transferiste (mínimo 3 caracteres)')
      .max(80, 'Máximo 80 caracteres'),
    observaciones: z.string().max(500, 'Máximo 500 caracteres').optional().or(z.literal('')),
  });
}

type FormValues = z.infer<ReturnType<typeof makeSchema>>;

interface Props {
  open: boolean;
  cupon: Cupon;
  onClose: () => void;
  /** Callback opcional al informar exitosamente. */
  onSuccess?: () => void;
}

export default function InformarPagoDialog({ open, cupon, onClose, onSuccess }: Props) {
  const informar = useInformarPagoEmpresa();

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const schema = useMemo(() => makeSchema(cupon), [cupon]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      monto_pagado: cupon.monto_total,
      fecha_pago: todayLocal(),
      nro_comprobante: '',
      medio_pago: '',
      observaciones: '',
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        monto_pagado: cupon.monto_total,
        fecha_pago: todayLocal(),
        nro_comprobante: '',
        medio_pago: '',
        observaciones: '',
      });
      setFile(null);
      setFileError(null);
      informar.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cupon.monto_total, reset]);

  const monto = watch('monto_pagado');
  const esPago = typeof monto === 'number' && !isNaN(monto);
  const esParcial = esPago && monto < cupon.monto_total;
  const esExcedente = esPago && monto > cupon.monto_total;

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFileError(null);
    if (!f) {
      setFile(null);
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setFileError('El archivo supera los 10 MB.');
      return;
    }
    if (f.type && !TIPOS_OK.includes(f.type)) {
      setFileError('Formato no admitido. Subí un PDF, PNG o JPG.');
      return;
    }
    setFile(f);
  }

  const onSubmit = async (values: FormValues) => {
    setFileError(null);
    if (!file) {
      setFileError('Adjuntá el comprobante de la transferencia (obligatorio).');
      return;
    }
    try {
      await informar.mutateAsync({
        cupon_id: cupon.id,
        empresa_id: cupon.empresa_id,
        monto_pagado: values.monto_pagado,
        fecha_pago: values.fecha_pago,
        nro_comprobante: values.nro_comprobante,
        medio_pago: values.medio_pago,
        observaciones: values.observaciones || null,
        file,
      });
      onSuccess?.();
      onClose();
    } catch {
      // se muestra abajo
    }
  };

  const apiError = informar.error as Error | null;

  useEffect(() => {
    if (apiError) {
      // eslint-disable-next-line no-console
      console.error('[InformarPago] error al informar el pago:', apiError);
    }
  }, [apiError]);

  const ocupado = isSubmitting || informar.isPending;

  return (
    <Dialog open={open} onClose={ocupado ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle>Informar pago y subir comprobante</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info" icon={false}>
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  Cupón <strong>{cupon.codigo}</strong>
                </Typography>
                <Typography variant="body2">
                  Total del cupón: <strong>{formatCurrency(cupon.monto_total)}</strong>
                </Typography>
              </Stack>
            </Alert>

            <Alert severity="warning" sx={{ fontSize: 13 }}>
              Informá el pago <strong>después de transferir</strong>. Tu aviso queda{' '}
              <strong>pendiente</strong> hasta que ATSA verifique que la transferencia ingresó
              y la confirme; recién entonces el cupón figura como pagado.
            </Alert>

            {apiError && (
              <Alert severity="error">
                {mapSupabaseError(
                  apiError,
                  'No se pudo informar el pago. Volvé a intentar; si persiste, avisá a ATSA.',
                )}
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller
                name="monto_pagado"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))
                    }
                    type="number"
                    required
                    fullWidth
                    label="Monto transferido"
                    error={!!errors.monto_pagado}
                    helperText={
                      errors.monto_pagado?.message ??
                      (esParcial
                        ? `Pago parcial: faltarán ${formatCurrency(cupon.monto_total - monto)}`
                        : esExcedente
                          ? `Excedente: ${formatCurrency(monto - cupon.monto_total)}`
                          : 'Coincide con el total del cupón')
                    }
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                )}
              />
              <TextField
                {...register('fecha_pago')}
                type="date"
                required
                fullWidth
                label="Fecha de la transferencia"
                InputLabelProps={{ shrink: true }}
                error={!!errors.fecha_pago}
                helperText={errors.fecha_pago?.message}
              />
            </Stack>

            <TextField
              {...register('nro_comprobante')}
              required
              fullWidth
              label="N° de comprobante / transferencia"
              placeholder="Ej: 00012345"
              error={!!errors.nro_comprobante}
              helperText={
                errors.nro_comprobante?.message ??
                'Figura en el comprobante que te da tu banco o billetera.'
              }
            />

            <TextField
              {...register('medio_pago')}
              required
              fullWidth
              label="Banco o billetera de origen"
              placeholder="Ej: Banco Galicia, Mercado Pago"
              error={!!errors.medio_pago}
              helperText={errors.medio_pago?.message}
            />

            <Box>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                fullWidth
              >
                {file ? 'Reemplazar comprobante' : 'Adjuntar comprobante'}
                <input hidden type="file" accept={ACCEPT_TYPES} onChange={handleFile} />
              </Button>
              {file && (
                <Chip
                  sx={{ mt: 1 }}
                  icon={<CheckIcon />}
                  label={`${file.name} (${(file.size / 1024).toFixed(0)} KB)`}
                  color="success"
                  variant="outlined"
                  onDelete={() => setFile(null)}
                />
              )}
              {!file && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  Comprobante obligatorio (PDF, PNG o JPG · máx. 10 MB).
                </Typography>
              )}
              {fileError && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                  {fileError}
                </Typography>
              )}
            </Box>

            <TextField
              {...register('observaciones')}
              fullWidth
              multiline
              rows={2}
              label="Observaciones (opcional)"
              error={!!errors.observaciones}
              helperText={errors.observaciones?.message}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={ocupado}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={ocupado}
            startIcon={ocupado ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {ocupado ? 'Enviando...' : 'Informar pago'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
